<template>
  <div class="py-3">
    <h4 class="text-center mb-2">{{ $t('reports.unitInspections') }}</h4>
    <v-data-table
      :loading="loadingTable"
      :loading-text="$t('general.loading')"
      :headers="headers"
      :items="inspections"
      :server-items-length="totalItems"
      :options="optionsDataTable"
      :expanded.sync="expanded"
      :footer-props="{ 'items-per-page-options': [10, 15, 50] }"
      @update:options="onOptionsChange"
      item-key="id"
      dense
    >
      <template v-slot:item.number="{ item }">
        {{ item.number || 'N/A' }}
      </template>
      <template v-slot:item.finished_at="{ item }">
        {{ formatDateTime(item.finished_at) }}
      </template>
      <template v-slot:item.checklist="{ item }">
        {{ item.checklist || 'N/A' }}
      </template>
      <template v-slot:item.site="{ item }">
        {{ item.site || 'N/A' }}
      </template>
      <template v-slot:item.inspector="{ item }">
        {{ item.inspector || 'N/A' }}
      </template>
      <template v-slot:item.previous="{ item }">
        {{ item.previous ? formatDateTime(item.previous.finished_at) : '-' }}
      </template>
      <template v-slot:item.changes_count="{ item }">
        <span v-if="item.status === 'no_previous_inspection'" class="warning--text font-weight-bold">
          {{ $t('reports.noPreviousInspection') }}
        </span>
        <div v-else class="d-flex align-center justify-center">
          <v-chip small color="primary" outlined class="font-weight-bold">
            {{ item.changes_count || 0 }}
          </v-chip>
          <!-- Advertencia de inspección incompleta; se deja comentada por si se retoma
          <v-tooltip v-if="item.status === 'incomplete'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-on="on" v-bind="attrs" small color="warning" class="ml-2">fas fa-exclamation-triangle</v-icon>
            </template>
            <span>{{ incompleteLabel(item) }}</span>
          </v-tooltip>
          -->
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small color="primary" @click="viewInspection(item.id)" v-bind="attrs" v-on="on">
              <v-icon small>far fa-eye</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('general.view') }}</span>
        </v-tooltip>
        <v-tooltip v-if="hasChanges(item)" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small color="primary" @click="toggleExpand(item)" v-bind="attrs" v-on="on">
              <v-icon small>{{ isExpanded(item) ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('reports.showChanges') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="grey lighten-5 py-3">
          <h4 class="mb-2">{{ $t('reports.changesFound') }}</h4>
          <v-simple-table dense class="transparent">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>{{ $t('reports.inspectionPoint') }}</th>
                  <th>{{ $t('reports.evaluatedField') }}</th>
                  <th>{{ $t('reports.previousValue') }}</th>
                  <th>{{ $t('reports.currentValue') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="change in changeRows(item)" :key="change.key" :class="{ 'striped-row': change.group % 2 === 1 }">
                  <td v-if="change.span" :rowspan="change.span" class="grouped-cell">{{ change.point || 'N/A' }}</td>
                  <td>{{ change.field }}</td>
                  <td>{{ change.previous || 'N/A' }}</td>
                  <td>{{ change.current || 'N/A' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: 'tireChangeReportDetails',
  props: {
    unitId: {
      type: Number,
      required: true
    },
    // Mismos filtros con los que se consultó la tabla de unidades
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loadingTable: false,
      totalItems: 0,
      // El endpoint no recibe parámetros de orden, así que las columnas no son ordenables
      optionsDataTable: {
        itemsPerPage: 10,
        page: 1
      },
      expanded: [],
      inspections: []
    }
  },
  watch: {
    // La tabla padre reutiliza este componente al volver a filtrar
    unitId () {
      this.reload()
    },
    filters () {
      this.reload()
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('general.noInspection'), align: 'center', value: 'number', sortable: false },
        { text: this.$t('general.inspectionDate'), align: 'center', value: 'finished_at', sortable: false },
        { text: this.$t('general.checklistName'), align: 'center', value: 'checklist', sortable: false },
        { text: this.$t('reports.yard'), align: 'center', value: 'site', sortable: false },
        { text: this.$t('general.inspector'), align: 'center', value: 'inspector', sortable: false },
        { text: this.$t('reports.previousInspectionDate'), align: 'center', value: 'previous', sortable: false },
        { text: this.$t('reports.changes'), align: 'center', value: 'changes_count', sortable: false },
        { text: this.$t('general.actions'), align: 'center', value: 'actions', sortable: false }
      ]
    }
  },
  methods: {
    onOptionsChange (options) {
      // v-data-table emite update:options al montarse, eso dispara la primera carga
      this.optionsDataTable = options
      this.get()
    },
    reload () {
      // Si la página cambia, la tabla emite update:options y dispara get()
      if (this.optionsDataTable.page === 1) return this.get()
      this.optionsDataTable = { ...this.optionsDataTable, page: 1 }
    },
    get () {
      this.expanded = []
      const { page, itemsPerPage } = this.optionsDataTable

      this.loadingTable = true
      this.$store.dispatch('reports/getTireChangeReportDetails', {
        event: {
          context: this,
          unitId: this.unitId,
          params: {
            ...this.filters,
            perPage: itemsPerPage,
            page
          }
        }
      })
    },
    hasChanges (item) {
      return !!item.changes && item.changes.length > 0
    },
    isExpanded (item) {
      return this.expanded.some(row => row.id === item.id)
    },
    // Vuetify solo entrega expand() en el slot item completo, no en los de columna,
    // así que el renglón de cambios se abre y cierra sobre el arreglo expanded
    toggleExpand (item) {
      this.expanded = this.isExpanded(item)
        ? this.expanded.filter(row => row.id !== item.id)
        : [...this.expanded, item]
    },
    // Una fila por cada campo modificado del punto de inspección; el punto
    // solo va en la primera fila del grupo y abarca (rowspan) todos sus campos
    changeRows (item) {
      if (!item.changes) return []

      // group alterna el color por punto de inspección, no por fila
      return item.changes.reduce((rows, change, group) => {
        const fields = change.fields || {}
        const keys = Object.keys(fields)
        keys.forEach((key, index) => {
          rows.push({
            key: `${change.question_id}-${key}`,
            group,
            span: index === 0 ? keys.length : 0,
            point: change.point,
            field: this.fieldLabel(key),
            previous: fields[key].previous,
            current: fields[key].current
          })
        })
        return rows
      }, [])
    },
    fieldLabel (key) {
      const labels = {
        brand: this.$t('general.brand'),
        model: this.$t('general.model'),
        folio: this.$t('inspections.folio')
      }
      return labels[key] || key
    },
    // Usado por la advertencia de inspección incompleta, hoy comentada en el template
    incompleteLabel (item) {
      const issues = item.issues || []
      if (issues.length === 0) return this.$t('reports.incompleteInspection')
      return this.$tc('reports.missingFieldsCount', issues.length, { count: issues.length })
    },
    viewInspection (id) {
      this.$router.push(`/reports/tireChangeReport/view/${id}`)
    },
    formatDateTime (value) {
      if (!value) return 'N/A'

      const date = new Date(value)
      if (isNaN(date.getTime())) return value

      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
/* Celdas agrupadas: el separador inferior debe verse aunque abarquen varias filas */
.grouped-cell {
  vertical-align: middle;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}

.striped-row {
  background-color: #eeeeee;
}
</style>
